import { useState, useEffect } from 'react';
import { useGetAvatarProjectDetails } from './use-get-avatar-project-details';
import { BannerMediaFiles, EditorModel } from '../../../../../types/brands/banner-media';
import { resolveVideoDuration } from '../utils/resolve-video-duration';
import { parseVTT, type Cue } from '../utils/parse-vtt';
import { parseTranscribeJson } from '../utils/parse-transcribe-json';
import { resolveAudioDuration } from '../utils/resolve-audio-duration';

export type AvatarsScene = {
  /** UUID v4 estable de la scene (se persiste en DB, sobrevive edits). */
  sceneId: string;
  /** Posición en el timeline (0-based). Se ordena por acá en el editor. */
  index: number;
  /** URL del video de HeyGen. null en escenas onlyVoice (solo audio). */
  videoUrl: string | null;
  /** URL del audio TTS. Presente en TODAS las escenas (HeyGen o onlyVoice). */
  audioUrl: string | null;
  thumbnailUrl: string | null;
  durationMs: number;
  script: string;
  subtitleUrl: string | null;
  subtitleCues: Cue[] | null;
  onlyVoice: boolean;
};

/**
 * Fetchea subtítulos: intenta JSON crudo de Transcribe (karaoke word-level) primero,
 * falls back al VTT hermano (misma URL + `.vtt`) si el JSON no está o no parsea.
 * Devuelve null si ambos fallan — los subs son opcionales, no rompen la carga.
 */
const fetchAndParseSubtitles = async (subtitleUrl: string | null): Promise<Cue[] | null> => {
  if (!subtitleUrl) return null;

  try {
    const res = await fetch(subtitleUrl);
    if (res.ok) {
      const text = await res.text();
      const cues = parseTranscribeJson(text, 1);
      if (cues.length > 0) return cues;
    }
  } catch (err) {
    console.warn(`Fetch/parse JSON falló para ${subtitleUrl}, probando VTT:`, err);
  }

  const vttUrl = `${subtitleUrl}.vtt`;
  try {
    const res = await fetch(vttUrl);
    if (!res.ok) {
      console.warn(`Fetch VTT falló para ${vttUrl}: ${res.status}`);
      return null;
    }
    const text = await res.text();
    return parseVTT(text);
  } catch (err) {
    console.warn(`Error resolviendo VTT ${vttUrl}:`, err);
    return null;
  }
};

export type AvatarsProject = {
  projectId: string;
  contentId: string | null;
  name: string;
  dimension: { width: number; height: number };
  scenes: AvatarsScene[];
  /** PieceModel guardado, en S3. */
  editorModel: EditorModel | null;
  /** @deprecated Canvas JSON inline de los proyectos previos a editorModel. */
  editorState: Record<string, unknown> | null;
  updatedAt: string | null;
  mediaFiles: BannerMediaFiles[] | null;
  placaId: string | null;
  /** Track de música elegido por el user en Studio. null = "Sin música" o proyecto viejo. */
  musicTrackId: string | null;
};

const DEFAULT_DURATION_MS = 10_000;

export const useAvatarsProject = (projectId?: string) => {
  const { getProjectDetails } = useGetAvatarProjectDetails();
  const [project, setProject] = useState<AvatarsProject | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setIsLoading(false);
      setError(new Error('projectId no proporcionado'));
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      const res = await getProjectDetails(projectId);
      if (cancelled) return;

      if (!res) {
        setError(new Error('No se pudo obtener el proyecto'));
        setIsLoading(false);
        return;
      }

      // Filtrar escenas completadas — aceptamos las que tienen video (HeyGen)
      // O las que tienen solo audio (onlyVoice). El editor renderiza cada una distinto.
      const completedScenes = res.scenes.filter(s => s.status === 'completed' && (s.videoUrl || s.audioUrl));

      // Resolver duraciones faltantes + VTTs en paralelo.
      // Duración: DDB → video → audio → default. VTT se resuelve en paralelo.
      const scenes: AvatarsScene[] = await Promise.all(
        completedScenes.map(async s => {
          const subtitleUrl = s.subtitleUrl ?? null;

          const resolveDuration = async (): Promise<number> => {
            if (s.durationMs != null) return s.durationMs;
            try {
              if (s.videoUrl) return await resolveVideoDuration(s.videoUrl);
              if (s.audioUrl) return await resolveAudioDuration(s.audioUrl);
            } catch (err) {
              console.warn(`Fallback de duración falló para escena ${s.sceneId}`, err);
            }
            return DEFAULT_DURATION_MS;
          };

          const [resolvedDuration, subtitleCues] = await Promise.all([
            resolveDuration(),
            fetchAndParseSubtitles(subtitleUrl)
          ]);

          return {
            sceneId: s.sceneId,
            index: s.index,
            videoUrl: s.videoUrl ?? null,
            audioUrl: s.audioUrl ?? null,
            thumbnailUrl: s.thumbnailUrl || null,
            durationMs: resolvedDuration ?? DEFAULT_DURATION_MS,
            script: s.script,
            onlyVoice: s.onlyVoice === true,
            subtitleUrl,
            subtitleCues
          };
        })
      );

      if (cancelled) return;

      setProject({
        projectId: res.projectId,
        contentId: res.contentId,
        name: res.name,
        dimension: res.dimension,
        scenes,
        editorModel: res.editorModel,
        editorState: res.editorState,
        updatedAt: res.updatedAt,
        mediaFiles: res.mediaFiles ?? null,
        placaId: res.placaId ?? null,
        musicTrackId: res.musicTrackId ?? null
      });
      setIsLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [projectId, getProjectDetails]);

  return { project, isLoading, error };
};
